import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Send, X, Monitor } from 'lucide-react';
import type { MensajeChatVista, UsuarioEnLineaVista } from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';

/**
 * Sprint 136 — Hablar sin salirse del sistema, y saber que hay alguien del otro lado.
 *
 * El caso, textual del dueño: *"Claudia me dice que está en problemas… y ya dentro del POS poder
 * platicar sin que tenga que salirse al WhatsApp"*.
 *
 * ## Qué hace y qué no
 *
 * **No reemplaza a WhatsApp, y hay que decirlo dentro de la pantalla.** Si nadie tiene el POS
 * abierto, aquí no suena nada: para *"¿estás?"* sigue siendo el celular. Esto sirve cuando los dos
 * ya están dentro — y ahí sí gana, porque salir a otra app a media captura significa soltar el
 * celular, cambiar de contexto y perder de vista el pedido que se está tomando.
 *
 * Lo que puede hacer y WhatsApp no: **decir desde dónde se escribe**. Media docena de
 * conversaciones de esta semana empezaron con ella describiendo en qué pantalla estaba.
 *
 * ## Por qué sondeo y no conexión persistente
 *
 * Porque la red del cliente se cae a ratos —la troncal de Telmex lo demostró esta semana— y una
 * conexión colgada se muere en silencio mientras un sondeo se reanuda solo. Se pregunta cada 15
 * segundos con el panel cerrado y cada 5 con el panel abierto: suficiente para una conversación
 * y muy por debajo de lo que costaría mantener el socket vivo.
 */

const CADA_CERRADO_MS = 15_000;
const CADA_ABIERTO_MS = 5_000;

export function PanelChat() {
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<MensajeChatVista[]>([]);
  const [enLinea, setEnLinea] = useState<UsuarioEnLineaVista[]>([]);
  const [sinLeer, setSinLeer] = useState(0);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const sesion = useSessionStore((s) => s.sesion);
  const location = useLocation();
  const finRef = useRef<HTMLDivElement | null>(null);

  const latido = useCallback(async () => {
    const r = await window.api.presencia.marcar(location.pathname);
    if (r.ok) {
      setEnLinea(r.data.enLinea);
      setSinLeer(r.data.sinLeer);
    }
  }, [location.pathname]);

  const cargar = useCallback(async () => {
    const r = await window.api.chat.listar();
    if (r.ok) setMensajes(r.data);
  }, []);

  // Un solo temporizador: marca presencia y, si el panel está abierto, trae la conversación.
  useEffect(() => {
    void latido();
    const cada = abierto ? CADA_ABIERTO_MS : CADA_CERRADO_MS;
    const id = setInterval(() => {
      void latido();
      if (abierto) void cargar();
    }, cada);
    return () => clearInterval(id);
  }, [abierto, latido, cargar]);

  useEffect(() => {
    if (!abierto) return;
    void cargar();
    void window.api.chat.marcarLeidos().then(() => setSinLeer(0));
  }, [abierto, cargar]);

  useEffect(() => {
    if (abierto) finRef.current?.scrollIntoView({ block: 'end' });
  }, [mensajes, abierto]);

  const enviar = async (contexto?: string) => {
    const t = texto.trim();
    if (!t && !contexto) return;
    setEnviando(true);
    const r = await window.api.chat.enviar({
      texto: t || 'Te comparto lo que estoy viendo:',
      pantalla: location.pathname,
      contexto,
    });
    setEnviando(false);
    if (r.ok) {
      setTexto('');
      await cargar();
    }
  };

  /**
   * "Enséñame lo que ves" — el estado de la pantalla, en texto.
   *
   * No es una captura de imagen, y es a propósito: una foto muestra el síntoma; esto muestra la
   * causa (qué campos tiene llenos, con qué valores, qué error salió). Además no pide permisos al
   * navegador, no arrastra una dependencia de 200 KB y pesa dos kilobytes — que con esta red es
   * la diferencia entre que llegue y que no.
   */
  const enviarLoQueVeo = async () => {
    const campos: string[] = [];
    document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input, textarea, select',
    ).forEach((el) => {
      if (el.type === 'password' || el.type === 'hidden') return;
      const etiqueta =
        el.getAttribute('aria-label') ??
        (el.id ? document.querySelector(`label[for="${el.id}"]`)?.textContent?.trim() : null) ??
        el.getAttribute('placeholder') ??
        el.name;
      const valor = 'value' in el ? String(el.value) : '';
      if (!etiqueta || !valor) return;
      /*
        Sprint 167 (auditoría `cyber`, M-2) — Lo que escribe la cajera de un cliente (su nombre, su
        WhatsApp, su dirección) no viaja al chat: se sabe que el campo estaba lleno, no qué decía. Un
        dato de cliente guardado en el chat queda fuera del aviso de privacidad y del borrado.
      */
      const esDelCliente =
        el.type === 'tel' ||
        !!el.closest('[data-pii]') ||
        /nombre|tel[eé]fono|whats|direcci[oó]n|calle|referencia|cliente|domicilio|ubicaci[oó]n/i.test(etiqueta);
      campos.push(`${etiqueta.replace(/\s+/g, ' ')}: ${esDelCliente ? '(dato del cliente, no se envía)' : valor}`);
    });

    const errores = [...document.querySelectorAll('[role="alert"], .error-text')]
      .map((e) => e.textContent?.trim())
      .filter((t): t is string => !!t);

    const titulo = document.querySelector('h1, h2, h3')?.textContent?.trim() ?? '';

    const contexto = [
      `Pantalla: ${location.pathname}`,
      titulo ? `Título: ${titulo}` : '',
      campos.length ? `Lo que tiene escrito:\n- ${campos.slice(0, 25).join('\n- ')}` : 'Sin campos con datos.',
      errores.length ? `Avisos en pantalla:\n- ${errores.join('\n- ')}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    await enviar(contexto);
  };

  const alguienEnLinea = enLinea.length > 0;

  return (
    <>
      <button
        onClick={() => setAbierto((v) => !v)}
        className="btn-ghost relative min-h-[40px] border border-slate-300 text-sm"
        title={
          alguienEnLinea
            ? `${enLinea.map((u) => u.nombre).join(', ')} en línea`
            : 'Nadie más está conectado ahora'
        }
      >
        <MessageCircle className="h-4 w-4" />
        Mensajes
        {alguienEnLinea && (
          <span className="ml-1 h-2 w-2 rounded-full bg-emerald-500" aria-hidden />
        )}
        {sinLeer > 0 && (
          <span className="ml-1 rounded-full bg-red-600 px-1.5 text-xs font-bold text-white">
            {sinLeer}
          </span>
        )}
      </button>

      {abierto && (
        <div className="fixed bottom-4 right-4 z-50 flex max-h-[70vh] w-[22rem] flex-col rounded-xl border border-slate-300 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
            <div>
              <div className="font-bold">Mensajes</div>
              <div className="text-xs text-slate-600">
                {alguienEnLinea ? (
                  enLinea.map((u) => (
                    <span key={u.usuarioId} className="mr-2">
                      🟢 {u.nombre}
                      {u.pantalla ? ` · en ${u.pantalla.replace('/', '')}` : ''}
                    </span>
                  ))
                ) : (
                  <span>Nadie más conectado ahora</span>
                )}
              </div>
            </div>
            <button onClick={() => setAbierto(false)} className="btn-ghost p-2" aria-label="Cerrar">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 space-y-2 overflow-auto px-3 py-3">
            {mensajes.length === 0 && (
              <p className="text-sm text-slate-600">
                Aquí pueden escribirse mientras los dos están dentro del sistema. Si necesitas
                avisar cuando nadie está conectado, sigue siendo mejor el WhatsApp.
              </p>
            )}
            {mensajes.map((m) => {
              const mio = m.deUsuarioId === sesion?.usuarioId;
              return (
                <div key={m.id} className={`flex ${mio ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      mio ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'
                    }`}
                  >
                    {!mio && <div className="text-xs font-semibold opacity-80">{m.deNombre}</div>}
                    <div className="whitespace-pre-wrap">{m.texto}</div>
                    {m.contexto && (
                      <pre
                        className={`mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded p-2 text-[11px] ${
                          mio ? 'bg-blue-700/50' : 'bg-white'
                        }`}
                      >
                        {m.contexto}
                      </pre>
                    )}
                    <div className={`mt-0.5 text-[10px] ${mio ? 'text-blue-100' : 'text-slate-500'}`}>
                      {new Date(m.fecha).toLocaleTimeString('es-MX', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {m.pantalla ? ` · ${m.pantalla.replace('/', '')}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={finRef} />
          </div>

          <div className="space-y-2 border-t border-slate-200 px-3 py-2">
            <button
              onClick={() => void enviarLoQueVeo()}
              className="btn-ghost min-h-[36px] w-full justify-center border border-slate-300 text-xs"
              disabled={enviando}
              title="Manda lo que tienes en pantalla: campos, valores y avisos"
            >
              <Monitor className="h-4 w-4" />
              Enséñame lo que ves
            </button>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void enviar();
                  }
                }}
                placeholder="Escribe aquí…"
                maxLength={2000}
              />
              <button
                onClick={() => void enviar()}
                className="btn-primary min-h-[44px] px-3"
                disabled={enviando || texto.trim().length === 0}
                aria-label="Enviar"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
